import axios from "axios"
import apiClient, { urls, TOKEN_KEY } from "./constants"

export const getEventsDetails = async (eventId: string) => {
  const response = await apiClient.get(urls.getEventDetails + eventId)
  return response.data
}

export const loginSendOTP = async (number: string) => {
  const response = await apiClient.post(urls.loginSendOTP, {
    phoneNumber: number,
  })
  return response.data
}

export const verifyOTP = async (number: string, otp: string) => {
  const response = await apiClient.post(urls.verifyOTP, {
    phoneNumber: number,
    otp,
  })
  const data = response.data
  if (data?.body?.token) {
    localStorage.setItem(TOKEN_KEY, data.body.token)
  }
  return data
}

export const loginWithEmail = async (email: string, password: string) => {
  const response = await apiClient.post(urls.emailLogin, { email, password })
  const data = response.data
  if (data?.body?.token) {
    localStorage.setItem(TOKEN_KEY, data.body.token)
  }
  return data
}

export const registerWithEmail = async (
  email: string,
  password: string,
  firstName: string,
  lastName: string
) => {
  const response = await apiClient.post(urls.emailRegister, {
    email,
    password,
    firstName,
    lastName,
  })
  const data = response.data
  if (data?.body?.token) {
    localStorage.setItem(TOKEN_KEY, data.body.token)
  }
  return data
}

export const loginWithGoogle = async (idToken: string) => {
  const response = await apiClient.post(urls.googleVerify, { idToken })
  const data = response.data
  if (data?.body?.token) {
    localStorage.setItem(TOKEN_KEY, data.body.token)
  }
  return data
}

export const clearAuthToken = () => {
  localStorage.removeItem(TOKEN_KEY)
}

export const getSubscriptionEventConfig = async (eventId: string) => {
  const response = await apiClient.get(
    urls.getSubscriptionEventConfig + eventId
  )
  return response.data
}

export const getUserByPhoneNumber = async (number: string) => {
  const response = await apiClient.get(urls.getUserByPhoneNumber + number)
  return response.data
}

export const getAllFilesOfEvents = async (eventId: string) => {
  const response = await apiClient.get(urls.getAllFilesOfEvents + eventId)
  return response.data
}

export const getFoldersOfEvent = async (eventId: string) => {
  const response = await apiClient.get(urls.getFoldersOfEvent + eventId)
  return response.data
}

export const listPhysicalAlbums = async (eventId: string, userId?: string) => {
  const response = await apiClient.get(
    `${urls.listPhysicalAlbums}?eventId=${encodeURIComponent(eventId)}${
      userId ? `&userId=${encodeURIComponent(userId)}` : ""
    }`
  )
  return response.data
}

export const getPhysicalAlbumFiles = async (
  physicalAlbumId: string,
  userId?: string
) => {
  const response = await apiClient.get(
    `${urls.physicalAlbumFiles}?physicalAlbumId=${encodeURIComponent(
      physicalAlbumId
    )}${userId ? `&userId=${encodeURIComponent(userId)}` : ""}`
  )
  return response.data
}

export const updateFilesInPhysicalAlbum = async (body: {
  userId: string
  physicalAlbumId: string
  fileIds: Array<string>
  addToPhysicalAlbum: boolean
}) => {
  const response = await apiClient.post(urls.physicalAlbumFiles, body)
  return response.data
}

export const deleteFileFromEvent = async (body: { fileIds: Array<string> }) => {
  const response = await apiClient.post(urls.deleteFileFromEvent, body)
  return response.data
}

export const moveFileToFolder = async (body: {
  folderId: string
  fileIds: Array<string>
}) => {
  const response = await apiClient.post(urls.move, body)
  return response.data
}

export const joinEvent = async (eventId: string, userId: string) => {
  const response = await apiClient.post(`${urls.joinEvent}?eventId=${encodeURIComponent(eventId)}&userId=${encodeURIComponent(userId)}`)
  return response.data
}

export const searchPhotosByFace = async (eventId: string, file: File) => {
  const formData = new FormData()
  formData.append('file', file)

  const response = await apiClient.post(urls.aiSearch + eventId, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return response?.data?.body
}

export const createProfile = async (number: string, firstName: string, lastName: string) => {
  const response = await apiClient.post(urls.createProfile, {
    phoneNumber: number,
    firstName,
    lastName,
    createdBy: "Gallery",
  })
  return response.data
}

// ---------------------------------------------------------------------------
// Direct-to-R2 upload flow (presign -> PUT bytes to presigned URLs -> confirm).
// The backend no longer receives file bytes; the browser uploads straight to
// R2/S3 via pre-signed PUT URLs.
// ---------------------------------------------------------------------------

export interface PresignFileMeta {
  clientRef: string
  fileName: string
  ext: string
  sizeBytes: number
}

export interface PresignUpload {
  clientRef: string
  fileId: string
  originalUploadUrl: string
  compressedUploadUrl: string | null
  indexUploadUrl: string | null
  expiresInSeconds: number
}

export const presignUploads = async (body: {
  eventId: string
  userId: string
  folderId: string | null
  files: PresignFileMeta[]
}) => {
  const response = await apiClient.post(urls.presignUploads, body)
  return response.data
}

export const confirmUploads = async (body: {
  eventId: string
  userId: string
  folderId: string | null
  files: Array<{ fileId: string; fileName: string; ext: string }>
}) => {
  const response = await apiClient.post(urls.confirmUploads, body)
  return response.data
}

// PUT raw bytes straight to a pre-signed R2/S3 URL. We use a bare axios call
// (not apiClient) on purpose: the URL is already signed, so it must NOT carry
// the apiClient Basic-auth header or baseURL — only the Content-Type.
export const putToPresignedUrl = async (
  url: string,
  body: Blob,
  contentType: string,
  onProgress?: (loaded: number, total: number) => void
) => {
  await axios.put(url, body, {
    headers: { "Content-Type": contentType },
    withCredentials: false,
    onUploadProgress: (event) => {
      if (onProgress) onProgress(event.loaded, event.total ?? body.size)
    },
  })
}

export const uploadFiles = async (
  files: File[],
  eventId: string,
  folderId: string,
  hostId: string,
  onProgress?: (progress: number) => void
) => {
  const formData = new FormData()
  files.forEach((file) => {
    formData.append('files', file)
  })

  const userId = hostId

  const response = await apiClient.post(
    `${urls.uploadFiles}?userId=${encodeURIComponent(userId)}&eventId=${encodeURIComponent(eventId)}${folderId ? `&folderId=${encodeURIComponent(folderId)}` : ''}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(progress)
        }
      },
    }
  )
  return response.data
}
